// One template: /growth/templates/new and /growth/templates/:id.
//
// The preview and the compliance check come from the server, so what's on screen
// is what the renderer would really do. The form saves as a whole, because a half
// edited subject and body aren't worth keeping apart.
//
// One kind: any template is a campaign step or a one-off email (a signup's
// profile, a tenant's Email tab). One list of merge fields, each email filling
// what it can; the check's campaign rules apply only when a campaign uses it.
//
// Under the form, the signature and footer every email gets (EmailFooterCard),
// and the preview shows the whole email as it goes, as a one-off (signed) or a
// campaign's (with the marketing footer).

import { useRef, useState, type FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api } from '@/api'
import { errorMessage, isApiError } from '@/api/client'
import { MERGE_FIELDS, type GrowthTemplate, type PreviewAs } from '@/api/growthCampaignTypes'
import { PageHeader } from '@/components/shell/PageHeader'
import {
  Badge,
  Button,
  ButtonLink,
  Card,
  CardHeader,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  FormField,
  InlineAlert,
  LoadingState,
  Textarea,
  TextInput,
  TrashIcon,
  useToast,
} from '@/components/ui'
import { useDebouncedValue, useDocumentTitle } from '@/lib/hooks'
import { invalidateQueries, useQuery } from '@/lib/query'
import { CheckList } from './campaignShared'
import { EmailFooterCard } from './EmailFooterCard'

const STARTER = {
  subject: 'A quick idea for {{league_name}}',
  body: 'Hi {{contact_name|there}},\n\nI work on ScoreLM, a website and live-stats platform for basketball leagues. I set up a preview of what {{league_name}} could look like:\n\n{{preview_url}}\n\nWorth a look?\n\nOmar',
}

function Preview({ subject, body }: { subject: string; body: string }) {
  const debounced = useDebouncedValue(JSON.stringify({ subject, body }), 300)
  const parsed = JSON.parse(debounced) as { subject: string; body: string }
  const empty = !parsed.subject.trim() && !parsed.body.trim()
  const [as, setAs] = useState<PreviewAs>('one_off')
  const { data, error, status, refetch } = useQuery(empty ? null : ['growth', 'template-preview', as, debounced], (signal) => api.growth.templates.preview({ ...parsed, as }, signal), { keepPrevious: true })
  const [view, setView] = useState<'text' | 'html'>('text')

  return (
    <div className="grid content-start gap-4">
      <Card>
        <CardHeader title="Check" description="Every email needs the first lines. The rest apply only when a campaign uses this template." />
        {data ? (
          <div className="grid gap-4">
            <CheckList checks={data.check.checks.filter((c) => !c.campaign)} />
            <div className="grid gap-2">
              <p className="text-2xs font-bold uppercase tracking-eyebrow text-muted">When a campaign uses it</p>
              <CheckList label="Campaign check" checks={data.check.checks.filter((c) => c.campaign)} />
            </div>
          </div>
        ) : status === 'error' ? (
          <ErrorState error={error} title="Couldn't check this" onRetry={refetch} />
        ) : (
          <p className="text-[13px] text-muted">Write a subject and a body to check them.</p>
        )}
      </Card>
      <Card>
        <CardHeader
          title="Preview"
          description={as === 'one_off' ? 'The whole email with sample values, as a one-off goes: signed by Omar, then the logo.' : "The whole email with sample values, as a campaign's goes: the logo, then the campaign footer."}
          actions={
            <div role="group" aria-label="Preview format" className="flex gap-1">
              <Button size="sm" variant={view === 'text' ? 'secondary' : 'ghost'} aria-pressed={view === 'text'} onClick={() => setView('text')}>
                Text
              </Button>
              <Button size="sm" variant={view === 'html' ? 'secondary' : 'ghost'} aria-pressed={view === 'html'} onClick={() => setView('html')}>
                HTML
              </Button>
            </div>
          }
        />
        <div role="group" aria-label="Preview as" className="mb-3 inline-flex rounded-md border border-line p-0.5">
          {(['one_off', 'campaign'] as const).map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={as === value}
              onClick={() => setAs(value)}
              className={`rounded px-2.5 py-1 text-xs font-semibold ${as === value ? 'bg-accent-soft text-accent' : 'text-muted hover:text-ink'}`}
            >
              {value === 'one_off' ? 'One-off email' : 'Campaign email'}
            </button>
          ))}
        </div>
        {data ? (
          <div className="grid gap-2">
            <p className="break-words text-[13.5px] font-semibold text-ink" data-testid="preview-subject">
              {data.subject}
            </p>
            {data.missing.length > 0 ? <p className="text-xs text-warn">No value for {data.missing.map((f) => `{{${f}}}`).join(', ')}. Add a fallback like {'{{contact_name|there}}'}, or an email without one can't go.</p> : null}
            {view === 'text' ? (
              <pre data-testid="preview-text" className="max-w-full overflow-x-hidden whitespace-pre-wrap break-words rounded-md bg-raised p-3 font-sans text-[13px] text-ink">
                {data.text}
              </pre>
            ) : (
              // sandbox with no permissions: nothing in this frame can run or navigate.
              <iframe title="HTML preview" sandbox="" srcDoc={data.html} className="h-80 w-full rounded-md border border-line bg-white" />
            )}
          </div>
        ) : (
          <p className="text-[13px] text-muted">The email appears here as you type.</p>
        )}
      </Card>
    </div>
  )
}

function FieldButtons({ onPick }: { onPick: (field: string) => void }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {MERGE_FIELDS.map((f) => (
        <button key={f} type="button" onClick={() => onPick(f)} className="rounded-md border border-line px-2 py-1 font-mono text-[11.5px] text-muted hover:border-line-strong hover:text-ink">
          {`{{${f}}}`}
        </button>
      ))}
    </div>
  )
}

function Editor({ template }: { template: GrowthTemplate | null }) {
  const navigate = useNavigate()
  const toast = useToast()
  const [name, setName] = useState(template?.name ?? '')
  const [subject, setSubject] = useState(template?.subject ?? STARTER.subject)
  const [body, setBody] = useState(template?.body ?? STARTER.body)
  const [saving, setSaving] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  const bodyRef = useRef<HTMLTextAreaElement>(null)

  const insert = (field: string) => {
    const el = bodyRef.current
    const at = el?.selectionStart ?? body.length
    setBody(`${body.slice(0, at)}{{${field}}}${body.slice(el?.selectionEnd ?? at)}`)
    el?.focus()
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setProblem(null)
    try {
      const write = { name: name.trim(), subject: subject.trim(), body: body.trim() }
      const saved = template ? await api.growth.templates.update(template.id, write) : await api.growth.templates.create(write)
      invalidateQueries('growth')
      toast.success(`${saved.name} is saved.`)
      if (!template) navigate(`/growth/templates/${saved.id}`)
    } catch (err) {
      setProblem(errorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="grid grid-cols-1 gap-5">
      <PageHeader
        crumbs={[{ label: 'Templates', to: '/growth/templates' }, { label: template?.name ?? 'New template' }]}
        title={template?.name ?? 'New template'}
        badges={
          template ? template.check.ok ? <Badge tone="good">Passes</Badge> : <Badge tone="bad">Needs work</Badge> : undefined
        }
        actions={
          template ? (
            <Button icon={<TrashIcon size={15} />} disabled={template.used_by > 0} title={template.used_by > 0 ? 'A campaign uses this template.' : undefined} onClick={() => setDeleting(true)}>
              Delete…
            </Button>
          ) : undefined
        }
      />
      <div className="grid items-start gap-4 lg:grid-cols-[1.2fr_1fr]">
        <div className="grid content-start gap-4">
          <Card>
          <form onSubmit={(e) => void submit(e)} className="grid gap-4">
            <FormField label="Name" hint="For you. Nobody who gets the email sees this.">
              {({ id }) => <TextInput id={id} value={name} maxLength={120} required onChange={(e) => setName(e.target.value)} />}
            </FormField>
            <FormField label="Subject">{({ id }) => <TextInput id={id} value={subject} maxLength={200} required onChange={(e) => setSubject(e.target.value)} />}</FormField>
            <FormField label="Body" hint="Plain text. A link on its own line works best.">
              {({ id }) => <Textarea id={id} ref={bodyRef} value={body} rows={12} maxLength={20000} required onChange={(e) => setBody(e.target.value)} />}
            </FormField>
            <div className="grid gap-1.5">
              <p className="text-xs font-semibold text-muted">Merge fields</p>
              <FieldButtons onPick={insert} />
              <p className="text-xs text-subtle">
                Each email fills what it can: a campaign the lead's, a one-off email the person's, their league's and the signer's. Add a fallback for a value that may be missing:{' '}
                <span className="font-mono">{'{{first_name|there}}'}</span>. A campaign can't use {'{{site_url}}'} or the signer's fields.
              </p>
            </div>
            {problem ? <InlineAlert tone="bad">{problem}</InlineAlert> : null}
            <div className="flex justify-end">
              <Button type="submit" variant="primary" loading={saving}>
                {template ? 'Save' : 'Create template'}
              </Button>
            </div>
          </form>
          </Card>
          <EmailFooterCard />
        </div>
        <Preview subject={subject} body={body} />
      </div>
      {template ? (
        <ConfirmDialog
          open={deleting}
          onClose={() => setDeleting(false)}
          title={`Delete ${template.name}?`}
          description="This removes the template."
          confirmLabel="Delete template"
          onConfirm={async () => {
            await api.growth.templates.delete(template.id)
            invalidateQueries('growth')
            navigate('/growth/templates')
          }}
        />
      ) : null}
    </div>
  )
}

export default function TemplateEditPage() {
  const { id: rawId } = useParams()
  const isNew = rawId === undefined
  const id = Number(rawId)
  const validId = isNew || (Number.isInteger(id) && id > 0)
  const { data, error, status, refetch } = useQuery(!isNew && validId ? ['growth', 'template', id] : null, (signal) => api.growth.templates.get(id, signal))
  useDocumentTitle(isNew ? 'New template' : (data?.name ?? 'Template'))

  if (isNew) return <Editor template={null} />
  if (!validId || (status === 'error' && isApiError(error) && error.code === 'not_found')) {
    return (
      <Card>
        <EmptyState title="Template not found" description="It may have been deleted." action={<ButtonLink to="/growth/templates">Back to templates</ButtonLink>} />
      </Card>
    )
  }
  if (status === 'loading' && !data) return <LoadingState label="Loading template…" />
  if (!data) {
    return (
      <Card>
        <ErrorState error={error} title="Couldn't load this template" onRetry={refetch} />
      </Card>
    )
  }
  // Editor seeds its form once, so a refetch can't replace what's being typed.
  return <Editor key={data.id} template={data} />
}
